// Admin → AI → AI Activity (Phase 1 quotation G3) — /api/admin/ai-activity, contract §6u.
import { api, type Paged } from '@/lib/api/client';

export type AgentHealth = 'healthy' | 'degraded' | 'off' | 'not_set_up';
export type AgentKind = 'ai' | 'rules' | 'planned';

export interface AgentSummary {
  id: string;
  label: string;
  kind: AgentKind;
  does: string;
  human: string;
  health: AgentHealth;
  calls: number;
  call_errors: number;
  avg_latency_ms: number | null;
  cost: number;
  currency: string | null;
  tasks: number;
  task_failures: number;
  needs_person: number;
  avg_task_ms: number | null;
  activity: { label: string; count: number } | null;
}

export interface ActivitySummary {
  ai_enabled: boolean;
  window: string;
  agents: AgentSummary[];
  queue: { running: number; pending: number; waiting_retry: number; needs_person: number; oldest_running_seconds: number | null };
  providers: { provider: string; calls: number; errors: number; last_error_at: string | null }[];
}

export type TaskStatus = 'pending' | 'running' | 'succeeded' | 'failed' | 'cancelled';

export interface AiTask {
  id: number;
  kind: string;
  kind_label: string;
  agent: string;
  subject_type: string;
  subject_id: number;
  status: TaskStatus;
  retry_safe: boolean;
  attempts: number;
  max_attempts: number;
  next_attempt_at: string | null;
  last_error: string | null;
  created_by: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  branch_name: string | null;
  uhid: string | null;
}

export interface AiTaskDetail extends AiTask {
  patient_name: string | null;
  result_summary: Record<string, unknown> | null;
  history: { at: string; event: string; error?: string; by?: string; retry_in_minutes?: number }[];
}

export type TaskFilter = '' | 'needs_person' | 'waiting_retry' | 'running' | 'succeeded' | 'cancelled';

export const fetchActivitySummary = (window: string) =>
  api.get<ActivitySummary>('/admin/ai-activity/summary', { query: { window } });

export const fetchTasks = (params: { agent?: string; status?: TaskFilter; page: number }) =>
  api.get<Paged<AiTask>>('/admin/ai-activity/tasks', {
    query: { agent: params.agent || undefined, status: params.status || undefined, page: params.page, limit: 25 },
  });

export const fetchTask = (id: number) => api.get<AiTaskDetail>(`/admin/ai-activity/tasks/${id}`);
export const retryTask = (id: number) => api.post<AiTask>(`/admin/ai-activity/tasks/${id}/retry`);
export const cancelTask = (id: number) => api.post<AiTask>(`/admin/ai-activity/tasks/${id}/cancel`);

/** A question ClintoBytes Copilot could not answer (G2 metrics catalogue) — stored masked, asker by role only. */
export type CopilotGapReason = 'no_tool' | 'unknown_metric' | 'unsupported_breakdown' | 'unsupported_filter';
export interface CopilotGap {
  id: number;
  question: string;
  reason: CopilotGapReason;
  detail: string | null;
  role: string | null;
  branch_name: string | null;
  created_at: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
}
export interface CopilotGaps {
  window: string;
  show: 'open' | 'all';
  total: number;
  by_reason: { reason: CopilotGapReason; n: number }[];
  items: CopilotGap[];
}

export const fetchCopilotGaps = (window: string, show: 'open' | 'all') =>
  api.get<CopilotGaps>('/admin/ai-activity/copilot-gaps', { query: { window, show } });
export const reviewCopilotGap = (id: number) =>
  api.post<{ id: number; reviewed_at: string; reviewed_by: string }>(`/admin/ai-activity/copilot-gaps/${id}/review`);

/** A task's state in words a non-engineer reads. */
export function taskState(t: Pick<AiTask, 'status' | 'next_attempt_at'>): { label: string; tone: 'success' | 'warn' | 'danger' | 'info' | 'neutral' } {
  if (t.status === 'succeeded') return { label: 'Done', tone: 'success' };
  if (t.status === 'running') return { label: 'Running', tone: 'info' };
  if (t.status === 'pending') return { label: 'Queued', tone: 'info' };
  if (t.status === 'cancelled') return { label: 'Cancelled', tone: 'neutral' };
  return t.next_attempt_at ? { label: 'Retrying soon', tone: 'warn' } : { label: 'Needs a decision', tone: 'danger' };
}
